package com.neurobs.ccab.db

import com.neurobs.ccab.db.TestRunManager.TestRunEvent
import com.neurobs.ccab.{Logfile, TestRunLogfileView, TestRunView}
import com.neurobs.util.{Guid, Logger, SecondsTimestamp}
import com.neurobs.errors._
import slick.jdbc.MySQLProfile.api._

import scala.concurrent.{ExecutionContext, Future}

class TestRunManager (dao: DAO, logger: Logger)(implicit ec: ExecutionContext) extends TestRunStorageBase(dao, logger) {

	import DAOImplicits._

	//val logfileStorages = new LogfileStorageStorageBase(dao, logger)
	val logfiles = new LogfileStorageBase(dao, logger)
	val testRunLogfiles = new TestRunLogfileStorageBase(dao, logger)

	private val hasTranscriptTestRunIdsFromSessionIdQuery = Compiled {
		(sessionId: Rep[Long]) => for {
			testTranscript <- dao.casr.testTranscriptTable
			testRun <- table
		if
			(testTranscript.testRunId === testRun.id) &&
			(testRun.sessionId === sessionId) &&
			testTranscript.transcript.isDefined
		} yield testRun.id
	}

	def hasTranscriptTestRunIdsFromSessionId(sessionId: Long): Future[Seq[Long]] = {
		database.run(hasTranscriptTestRunIdsFromSessionIdQuery(sessionId).result)
	}

	private val hasErrorTranscriptTestRunIdsFromSessionIdQuery = Compiled {
		(sessionId: Rep[Long]) => for {
			testTranscript <- dao.casr.testTranscriptTable
			testRun <- table
			if
				(testTranscript.testRunId === testRun.id) &&
					(testRun.sessionId === sessionId) &&
					testTranscript.transcript.isEmpty
		} yield testRun.id
	}

	def hasErrorTranscriptTestRunIdsFromSessionId(sessionId: Long): Future[Seq[Long]] = {
		database.run(hasErrorTranscriptTestRunIdsFromSessionIdQuery(sessionId).result)
	}

	private val fromSessionIdQuery = Compiled {
		(sessionId: Rep[Long]) => table.filter(_.sessionId === sessionId)
	}

	def fromSessionId(sessionId: Long): Future[Seq[TestRunData]] = {
		database.run(fromSessionIdQuery(sessionId).result)
	}

	def createLogfile(data: LogfileData, testRunId: Long): Future[LogfileData] = {
		logfiles.create(data) flatMap { logfile =>
			testRunLogfiles.create(TestRunLogfileData(testRunId = testRunId, logfileId = logfile.id)).map(_ => logfile)
		}
	}

	private val logfileFromTestRunIdQuery = Compiled {
		(testRunId: Rep[Long]) => for {
			logfile <- logfiles.table
			testRunLogfile <- testRunLogfiles.table
			if (testRunLogfile.testRunId === testRunId) && (logfile.id === testRunLogfile.logfileId)
		} yield logfile
	}

	def logfileFromTestRunId(testRunId: Long): Future[Seq[LogfileData]] = {
		database.run(logfileFromTestRunIdQuery(testRunId).result)
	}

	private val logfilesFromSessionIdQuery = Compiled {
		(sessionId: Rep[Long]) => for {
			testRun <- table
			testRunLogfile <- testRunLogfiles.table
			logfile <- logfiles.table
			if (testRunLogfile.testRunId === testRun.id) && (logfile.id === testRunLogfile.logfileId) && (testRun.sessionId === sessionId)
		} yield logfile
	}

	def logfilesFromSessionId(sessionId: Long): Future[Seq[LogfileData]] = {
		database.run(logfilesFromSessionIdQuery(sessionId).result)
	}

	private val logfileFromOrigFilenameQuery = Compiled {
		(origFilename: Rep[String]) => logfiles.table.filter(_.origFilename === origFilename)
	}

	def logfileFromOrigFilename(origFilename: String): Future[Option[LogfileData]] = {
		database.run(logfileFromOrigFilenameQuery(origFilename).result.headOption)
	}

	private val testRunHistoryFromAccountSubjectIdQuery = Compiled {
		(accountSubjectId: Rep[Long]) => for {
			run <- table
			session <- dao.data.sessionTable
			test <- dao.data.testTable
			if (run.sessionId === session.id) && (session.accountSubjectId === accountSubjectId) && (run.runError.isEmpty) && (run.testId === test.id)
		} yield (session.subjectGuid, run.startTime, test.identifier)
	}

	def testRunHistoryFromAccountSubjectId(accountSubjectId: Long): Future[Seq[TestRunEvent]] = {
		dao.database.run(testRunHistoryFromAccountSubjectIdQuery(accountSubjectId).result).map(_.map(TestRunEvent.tupled(_)))
	}

	private val testRunAccountLogfileByTestRunIdQuery = Compiled {
		(testRunId: Rep[Long]) => for {
			run <- table
			session <- dao.data.sessionTable
			testRunLogfile <- dao.data.testRunLogfileTable
			logfile <- dao.data.logfileTable
			if (run.sessionId === session.id) && (run.id === testRunId) && (testRunLogfile.testRunId === testRunId) && (logfile.id === testRunLogfile.logfileId)
		} yield (session.accountId, logfile.origFilename, logfile.startTime)
	}

	def testRunAccountLogfileByTestRunId(testRunId: Long): Future[Option[TestRunManager.TestRunAccountLogfile]] = {
		dao.database.run(testRunAccountLogfileByTestRunIdQuery(testRunId).result.headOption).map(_.map(TestRunManager.TestRunAccountLogfile.tupled(_)))
	}

	private val accountIdByTestRunIdQuery = Compiled {
		(testRunId: Rep[Long]) => for {
			run <- table
			session <- dao.data.sessionTable
			if (run.sessionId === session.id) && (run.id === testRunId)
		} yield session.accountId
	}

	def accountIdByTestRunId(testRunId: Long): Future[Option[Long]] = {
		database.run(accountIdByTestRunIdQuery(testRunId).result.headOption)
	}

	private val testRunViewFromErrorQuery = Compiled {
		(studyTypeId: Rep[Long], runError: Rep[Option[String]]) => for {
			testRun <- dao.data.testRunTable
			test <- dao.data.testTable
			session <- dao.data.sessionTable
			study <- dao.data.studyTable
			account <- dao.account.accountTable
			accountSubject <- dao.data.accountSubjectTable
			subject <- dao.data.subjectTable
			studyType <- dao.data.studyTypeTable
			if
				(testRun.testId === test.id) &&
				(testRun.sessionId === session.id) &&
				(session.studyId === study.id) &&
				(study.studyTypeId === studyType.id) &&
				(session.accountId === account.id) &&
				(session.accountSubjectId === accountSubject.id) &&
				(accountSubject.subjectId === subject.id) &&
				(studyType.id === studyTypeId) &&
				(testRun.runError === runError)
		} yield (
			subject.id,
			subject.birthYear,
			accountSubject.id,
			accountSubject.reportingId,
			account.id,
			account.primaryEmail,
			study.id,
			study.displayName,
			studyType.id,
			studyType.displayName,
			session.id,
			session.subjectGuid,
			session.properties,
			test.id,
			test.displayName,
			testRun.id,
			testRun.startTime,
			testRun.runError
		)
	}

	def testRunViewFromError(studyTypeId: Long, error: String): Future[Seq[TestRunView]] = {
		dao.database.run(testRunViewFromErrorQuery(studyTypeId, Some(error)).result).map(_.map(TestRunView.tupled(_)))
	}

	private val testRunLogfileViewsAfterTimestampQuery = {
		(after: Rep[SecondsTimestamp]) => for {
			testRun <- table.filter(_.createdAt >= after)
			testRunLogfile <- dao.data.testRunLogfileTable
			logfile <- dao.data.logfileTable
			if (testRun.id === testRunLogfile.testRunId) && (testRunLogfile.logfileId === logfile.id)
		} yield (
			testRun,
			logfile
		)
	}

	def testRunLogfileViewsAfterTimestamp(after: SecondsTimestamp): Future[Seq[TestRunLogfileView]] = {
		database.run(testRunLogfileViewsAfterTimestampQuery(after).result).map(_.map(TestRunLogfileView.tupled(_)))
	}

	private val testRunLogfileViewFromSessionIdQuery = {
		(sessionId: Rep[Long]) => for {
			testRun <- table
			testRunLogfile <- dao.data.testRunLogfileTable
		logfile <- dao.data.logfileTable
		if
			(testRun.sessionId === sessionId) &&
			(testRun.id === testRunLogfile.testRunId) &&
			(testRunLogfile.logfileId === logfile.id)
		} yield (
			testRun,
			logfile
		)
	}

	def testRunLogfileViewFromSessionId(sessionId: Long): Future[Seq[TestRunLogfileView]] = {
		database.run(testRunLogfileViewFromSessionIdQuery(sessionId).result).map(_.map(TestRunLogfileView.tupled(_)))
	}

	private val testRunLogfileViewFromLogfileFilenameQuery = {
		(filename: Rep[String]) => for {
			testRun <- table
			testRunLogfile <- dao.data.testRunLogfileTable
			logfile <- dao.data.logfileTable
			if
				(logfile.origFilename === filename) &&
					(testRun.id === testRunLogfile.testRunId) &&
					(testRunLogfile.logfileId === logfile.id)
		} yield (
			testRun,
			logfile
		)
	}

	def testRunLogfileViewFromLogfileFilename(logfileFilename: String): Future[Option[TestRunLogfileView]] = {
		database.run(testRunLogfileViewFromLogfileFilenameQuery(logfileFilename).result.headOption).map(_.map(TestRunLogfileView.tupled(_)))
	}

	private val testRunLogfileViewFromIdQuery = {
		(id: Rep[Long]) => for {
			testRun <- table
			testRunLogfile <- dao.data.testRunLogfileTable
			logfile <- dao.data.logfileTable
			if
				(testRun.id === id) &&
				(testRun.id === testRunLogfile.testRunId) &&
				(testRunLogfile.logfileId === logfile.id)
		} yield (
			testRun,
			logfile
		)
	}

	def testRunLogfileViewFromId(id: Long): Future[Option[TestRunLogfileView]] = {
		database.run(testRunLogfileViewFromIdQuery(id).result.headOption).map(_.map(TestRunLogfileView.tupled(_)))
	}

	private val testRunSpeechAudioFilesFromIdQuery = Compiled {
		(testRunId: Rep[Long]) => dao.casr.testRunSpeechAudioFileTable.filter(_.testRunId === testRunId)
	}

	def testRunSpeechAudioFilesFromId(testRunId: Long): Future[Seq[TestRunSpeechAudioFileData]] = {
		database.run(testRunSpeechAudioFilesFromIdQuery(testRunId).result)
	}

	def TestRunLogfileViewFromTestRunIds(testRunIds: Seq[Long]): Future[Seq[TestRunLogfileView]] = {
		val q = for {
			testRun <- table
			testRunLogfile <- testRunLogfiles.table
			logfile <- logfiles.table
			if
				(testRun.id.inSet(testRunIds)) &&
				(testRunLogfile.testRunId === testRun.id) &&
				(logfile.id === testRunLogfile.logfileId)
		} yield (
			testRun,
			logfile
		)
		database.run(q.result).map(a => a.map(TestRunLogfileView.tupled(_)))
	}
}

object TestRunManager {
	case class TestRunAccountLogfile
	(
		accountId: Long,
		origFilename: String,
		startTime: SecondsTimestamp
	)

	case class TestRunEvent
	(
		session: Guid,
		startTime: SecondsTimestamp,
		testIden: String
	)
}